import { Button, Dialog, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FileUploader } from "react-drag-drop-files";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Spinner from "../../components/spinner";
import MuiAlert from "@mui/material/Alert";
import Slide from "@mui/material/Slide";
import useCreate from "../../hooks/useCreate";
import useUploadMedia from "../../hooks/useUploadMedia";
import { toast } from "react-hot-toast";

const fileTypes = ["JPG", "PNG", "JPEG", "WEBP"];

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function EditBrand({ open, onClose, editData }) {
  const queryClient = useQueryClient();
  const initialState = {
    brand_name: "",
    brand_url: "",
    logo: "",
    brand_id: "",
    document_id: "",
  };
  const [brandData, setBrandData] = useState(initialState);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [logoChanged, setLogoChanged] = useState(false);

  const handleInputData = (e) => {
    let value = e.target.value;
    let data = {
      ...brandData,
      [e.target.name]: value,
    };
    setBrandData(data);
  };

  const handleuploadAgain = () => {
    let data = {
      ...brandData,
      logo_preview: "",
      logo: "",
      document_id: "",
    };
    setBrandData(data);
    setLogoChanged(true);
  };

  const handleMediaUploadSuccess = (data) => {
    // Store new image_id and image_url from the new response structure
    setBrandData((prev) => ({
      ...prev,
      document_id: data.data.image_id,
      logo_preview: data.data.image_url,
    }));
    setIsImageUploading(false);
    toast.success("Image uploaded successfully", {
      position: "top-right",
      duration: 2000,
    });
  };

  const handleMediaUploadError = (error) => {
    setIsImageUploading(false);
    toast.error(error?.response?.data?.message || "Error uploading image", {
      position: "top-right",
      duration: 3000,
    });
  };

  const { upload } = useUploadMedia({
    url: "upload/image",
    onSuccess: handleMediaUploadSuccess,
    onError: handleMediaUploadError,
  });

  const handleAddFromData = (e, file, isFileObject, name) => {
    let fileObject;
    if (file) {
      if (isFileObject) {
        fileObject = e;
      } else {
        fileObject = e.target.files[0];
      }

      setBrandData((prev) => ({
        ...prev,
        [name]: fileObject,
      }));

      // Mark logo as changed
      setLogoChanged(true);

      // Upload immediately
      setIsImageUploading(true);
      upload.mutate({ thumbnail: fileObject, screen: "BRAND" });
    } else {
      let value = e.target.value;
      setBrandData((prev) => ({
        ...prev,
        [e.target.name]: value,
      }));
    }
  };

  const handleCloseAndReset = () => {
    onClose();
    setIsSuccess(false);
    setIsError(false);
    setBrandData(initialState);
    setLogoChanged(false);
  };

  const handleBrandCreateSuccess = () => {
    handleCloseAndReset();
    toast.success("Brand Edited Successfully", {
      position: "top-right",
      duration: 3000,
    });
  };

  const handleBrandCreateError = (error) => {
    toast.error(error?.response?.data?.message || "Error updating brand", {
      position: "top-right",
      duration: 3000,
    });
  };

  const { setDataToServer: setBrandDataToServer } = useCreate({
    refreshUrl: "brand/list",
    url: "brand/update",
    onSuccess: handleBrandCreateSuccess,
    onError: handleBrandCreateError,
  });

  const handleEditBrand = (e) => {
    e.preventDefault();

    // Validate document_id is present
    // if (!brandData.document_id) {
    //   toast.error("Brand Logo is required", {
    //     position: 'top-right',
    //     duration: 3000
    //   })
    //   return
    // }
    const payloadData = {
      name: brandData.brand_name,
      brand_url: brandData.brand_url,
      brand_id: brandData.brand_id,
      logo: brandData.document_id,
    };

    setBrandDataToServer.mutate(payloadData);
  };

  const closeErrorMessage = () => {
    setIsError(false);
  };

  useEffect(() => {
    isError && setTimeout(() => setIsError(false), [4000]);
  }, [isError]);

  useEffect(() => {
    if (editData) {
      setBrandData({
        ...editData,
        brand_url: editData?.brand_url,
        logo_preview: editData?.logo_url || editData?.logo,
        document_id: editData?.logo || "", // Contains the document_id for the logo
      });
      setLogoChanged(false);
    }
  }, [editData]);

  return (
    <>
      <Dialog
        fullScreen
        maxWidth={"md"}
        open={open}
        onClose={handleCloseAndReset}
        TransitionComponent={Transition}
      >
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-40 bg-white">
            <div>
              <IconButton onClick={handleCloseAndReset}>
                <ArrowBackIcon />
              </IconButton>
            </div>
            <div>
              <h1 className="heading">Edit Brand</h1>
            </div>
          </div>
          <div className="form-body py-8">
            <form className=" space-y-4" onSubmit={handleEditBrand}>
              <div>
                <label htmlFor="Brand Name" className="label">
                  Brand Name <small className="text-red-700">*</small>
                </label>
                <input
                  required
                  type="text"
                  placeholder="Enter Brand"
                  name="brand_name"
                  className="input"
                  value={brandData?.brand_name}
                  onChange={handleInputData}
                />
              </div>
              <div>
                <label htmlFor="Brand URL" className="label">
                  Brand URL <small className="text-red-700">*</small>
                </label>
                <input
                  required
                  type="text"
                  placeholder="Enter Brand URL"
                  name="brand_url"
                  className="input"
                  value={brandData?.brand_url}
                  onChange={handleInputData}
                />
              </div>
              <div>
                <div className="my-7 flex items-center justify-between border-b border-gray-400">
                  <div className="pb-3">
                    <h1 className="font-semibold text-2xl">
                      Brand Logo <small className="text-red-700">*</small>
                    </h1>
                    <small className="text-red-700">
                      Note: Image to be less than 1mb
                    </small>
                  </div>
                </div>
                <div className=" space-y-4">
                  {isImageUploading ? (
                    <div className="text-center py-8">
                      <Spinner />
                      <p className="mt-2">Uploading image...</p>
                    </div>
                  ) : !brandData?.logo_preview ? (
                    <FileUploader
                      name="logo"
                      types={fileTypes}
                      handleChange={(e) =>
                        handleAddFromData(e, true, true, "logo")
                      }
                      classes="file-uploader"
                      hoverTitle="Drop here"
                    />
                  ) : (
                    <div className=" border border-dashed border-blue-700 rounded">
                      <div className=" w-40 mx-auto py-4 text-center space-y-3">
                        <img src={brandData?.logo_preview} alt="preview" />
                        <Button variant="outlined" onClick={handleuploadAgain}>
                          Re-Upload
                        </Button>
                        {brandData.document_id && logoChanged && (
                          <p className="text-green-600 text-xs">
                            New image uploaded ✓
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {isError && (
                <Alert
                  onClose={closeErrorMessage}
                  className="mb-4"
                  severity="error"
                  sx={{ width: "100%" }}
                >
                  {setBrandDataToServer?.error?.message}, Please try again later
                </Alert>
              )}
              {isSuccess && (
                <Alert
                  onClose={closeErrorMessage}
                  className="mb-4"
                  severity="success"
                  sx={{ width: "100%" }}
                >
                  Category Added Succesfully
                </Alert>
              )}
              <div className=" space-x-4 text-center pb-5">
                {setBrandDataToServer.isLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isImageUploading}
                    >
                      Save
                    </Button>
                    <Button variant="outlined" onClick={handleCloseAndReset}>
                      Close
                    </Button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default EditBrand;
